// Global search — the ⌘K palette. Jump to any artist (switches workspace) or
// page (opens the editor) from anywhere in the admin. Company-scoped server-side
// (/api/global-search). Keyboard-first: ⌘K / Ctrl+K opens, arrows move, Enter
// activates, Esc closes; the rail search button mirrors it for discoverability.
(function () {
  var overlay = null, input = null, list = null;
  var results = { artists: [], pages: [] };
  var flat = [];          // flattened result rows for keyboard navigation
  var selected = 0;
  var debounceT = null;
  var lastQuery = '';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function ensureDom() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'gsk-overlay';
    overlay.innerHTML =
      '<div class="gsk-panel" role="dialog" aria-label="Search">' +
        '<div class="gsk-input-row">' +
          '<i class="bi bi-search"></i>' +
          '<input type="text" class="gsk-input" placeholder="Search artists and pages…" spellcheck="false">' +
          '<span class="gsk-hint">esc</span>' +
        '</div>' +
        '<div class="gsk-list"></div>' +
      '</div>';
    document.body.appendChild(overlay);
    input = overlay.querySelector('.gsk-input');
    list = overlay.querySelector('.gsk-list');

    overlay.addEventListener('mousedown', function (e) { if (e.target === overlay) close(); });
    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(debounceT);
      if (q.length < 2) { renderEmpty(q ? 'Keep typing…' : null); return; }
      debounceT = setTimeout(function () { search(q); }, 180);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Enter') { e.preventDefault(); activate(selected); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
    });
  }

  // Keep the vertically-centred panel centred in the VISIBLE viewport when the mobile keyboard opens —
  // otherwise it centres in the full layout viewport and the search bar drops behind the keyboard (James).
  function syncViewport() {
    if (!overlay || !window.visualViewport) return;
    var vv = window.visualViewport;
    overlay.style.top = vv.offsetTop + 'px';
    overlay.style.height = vv.height + 'px';
    overlay.style.bottom = 'auto';
  }

  function open() {
    ensureDom();
    overlay.classList.add('is-open');
    document.body.classList.add('gsk-active'); // invert the page behind
    input.value = ''; lastQuery = '';
    renderEmpty(null);
    // Focus SYNCHRONOUSLY, inside the opening gesture — the old deferred focus (setTimeout) ran
    // outside the user-activation window, so iOS/iPad never raised the on-screen keyboard. A rAF
    // re-focus covers any open-transition timing without breaking the gesture-driven keyboard.
    input.focus({ preventScroll: true });
    requestAnimationFrame(function () { input.focus({ preventScroll: true }); });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', syncViewport);
      window.visualViewport.addEventListener('scroll', syncViewport);
    }
    syncViewport();
    setTimeout(syncViewport, 300); // recentre once the keyboard has animated in
  }
  function close() {
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('gsk-active');
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', syncViewport);
      window.visualViewport.removeEventListener('scroll', syncViewport);
    }
    if (overlay) { overlay.style.top = ''; overlay.style.height = ''; overlay.style.bottom = ''; }
  }
  function isOpen() { return overlay && overlay.classList.contains('is-open'); }

  function search(q) {
    lastQuery = q;
    fetch('/api/global-search?q=' + encodeURIComponent(q))
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (q !== lastQuery) return;   // stale response
        if (!d.success) { renderEmpty('Search failed - try again'); return; }
        results = { artists: d.artists || [], pages: d.pages || [] };
        render();
      })
      .catch(function () { renderEmpty('Search failed - try again'); });
  }

  function renderEmpty(msg) {
    flat = []; selected = 0;
    list.innerHTML = msg ? '<div class="gsk-empty">' + esc(msg) + '</div>' : '';
  }

  function thumb(src, icon, round) {
    return src
      ? '<img class="gsk-thumb' + (round ? ' gsk-thumb--round' : '') + '" src="' + esc(src) + '" alt="">'
      : '<span class="gsk-thumb' + (round ? ' gsk-thumb--round' : '') + ' gsk-thumb--ph"><i class="bi ' + icon + '"></i></span>';
  }

  function render() {
    flat = []; selected = 0;
    var html = '';
    if (results.artists.length) {
      html += '<div class="gsk-group">Artists</div>';
      results.artists.forEach(function (a) {
        flat.push({ kind: 'artist', item: a });
        html += '<div class="gsk-row" data-i="' + (flat.length - 1) + '">' +
          thumb(a.image, 'bi-person-fill', true) +
          '<div class="gsk-row-main"><div class="gsk-row-title">' + esc(a.name) + '</div>' +
          '<div class="gsk-row-sub">Artist workspace</div></div>' +
          '<i class="bi bi-arrow-return-left gsk-row-go"></i></div>';
      });
    }
    if (results.pages.length) {
      html += '<div class="gsk-group">Pages</div>';
      results.pages.forEach(function (p) {
        flat.push({ kind: 'page', item: p });
        var sub = (p.is_root ? 'Profile' : (p.page_type === 'bio' ? 'Bio' : 'Release')) +
          (p.status === 'active' ? ' · Live' : ' · Offline') +
          (p.product_artist ? ' · ' + p.product_artist : '');
        html += '<div class="gsk-row" data-i="' + (flat.length - 1) + '">' +
          thumb(p.image, p.page_type === 'bio' ? 'bi-person-square' : 'bi-disc', false) +
          '<div class="gsk-row-main"><div class="gsk-row-title">' + esc(p.title || 'Untitled') + '</div>' +
          '<div class="gsk-row-sub">' + esc(sub) + '</div></div>' +
          '<i class="bi bi-arrow-return-left gsk-row-go"></i></div>';
      });
    }
    if (!html) html = '<div class="gsk-empty">Nothing found for “' + esc(lastQuery) + '”</div>';
    list.innerHTML = html;
    paintSelection();
    list.querySelectorAll('.gsk-row').forEach(function (row) {
      row.addEventListener('click', function () { activate(parseInt(row.dataset.i, 10)); });
      row.addEventListener('mousemove', function () {
        var i = parseInt(row.dataset.i, 10);
        if (i !== selected) { selected = i; paintSelection(); }
      });
    });
  }

  function paintSelection() {
    list.querySelectorAll('.gsk-row').forEach(function (row) {
      row.classList.toggle('is-selected', parseInt(row.dataset.i, 10) === selected);
    });
    var el = list.querySelector('.gsk-row.is-selected');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }

  function move(d) {
    if (!flat.length) return;
    selected = (selected + d + flat.length) % flat.length;
    paintSelection();
  }

  function activate(i) {
    var entry = flat[i];
    if (!entry) return;
    close();
    if (entry.kind === 'artist') {
      if (window.ArtistSwitcher && ArtistSwitcher.select) ArtistSwitcher.select(entry.item.id);
      if (window.location.pathname !== '/pages') {
        if (window.SPANavigation && SPANavigation.navigateTo) SPANavigation.navigateTo('/pages');
        else window.location.href = '/pages';
      }
    } else {
      var p = entry.item;
      // Keep the workspace in sync with the page being opened, then open the editor
      var cur = null;
      try { cur = localStorage.getItem('selectedArtistId'); } catch (e) {}
      if (String(cur) !== String(p.artist_id) && window.ArtistSwitcher && ArtistSwitcher.select) {
        ArtistSwitcher.select(p.artist_id, { silent: true });
      }
      if (typeof SidebarDetail !== 'undefined' && SidebarDetail.openInEditMode) {
        SidebarDetail.openInEditMode(p.id, { hints: { artistSlug: p.artist_slug, slug: p.slug } });
      }
    }
  }

  // ⌘K / Ctrl+K anywhere (including inputs — it's the universal palette chord)
  document.addEventListener('keydown', function (e) {
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      isOpen() ? close() : open();
    }
  });

  // Rail button (wired lazily — the sidebar renders before this script on SPA boots)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('#globalSearchBtn');
    if (btn) { e.preventDefault(); open(); }
  });

  window.GlobalSearch = { open: open, close: close };
})();
